import { useState } from "react";
import type { Column } from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DocumentSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { cn } from "~/lib/utils";
import type { ResolvedDocumentsColumnGroup } from "./columnRegistry";

interface DocumentsColumnsMenuProps {
  ungroupedColumns: Column<DocumentSummary, unknown>[];
  resolvedGroups: ResolvedDocumentsColumnGroup[];
  columnMenuLabels: Record<string, string>;
}

export const DocumentsColumnsMenu = ({
  ungroupedColumns,
  resolvedGroups,
  columnMenuLabels,
}: DocumentsColumnsMenuProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="shrink-0">
          {t(($) => $.library.columns)}
          <ChevronRight
            className={cn("transition-transform", isOpen && "rotate-90")}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {ungroupedColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            className="capitalize"
            checked={column.getIsVisible()}
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(value) => column.toggleVisibility(!!value)}
          >
            {columnMenuLabels[column.id]}
          </DropdownMenuCheckboxItem>
        ))}

        {resolvedGroups.length > 0 && <DropdownMenuSeparator />}
        {resolvedGroups.map((group, index) => {
          const visibleColumns = group.columns.filter((column) =>
            column.getIsVisible(),
          ).length;
          const groupChecked =
            visibleColumns === 0
              ? false
              : visibleColumns === group.columns.length
                ? true
                : "indeterminate";

          return (
            <div key={group.id}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuCheckboxItem
                checked={groupChecked}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(value) => {
                  const shouldShow = value === true;
                  group.columns.forEach((column) =>
                    column.toggleVisibility(shouldShow),
                  );
                }}
              >
                {group.label}
              </DropdownMenuCheckboxItem>
              {group.columns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="text-muted-foreground pl-4"
                  checked={column.getIsVisible()}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {columnMenuLabels[column.id]}
                </DropdownMenuCheckboxItem>
              ))}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
